import { postJson, send } from 'helpers/http';

import type {
    ForgotPasswordRequest,
    LoginProvidersResponse,
    LoginRequest,
    LoginResponse,
    PolicyDocumentAcceptance,
    PrivacyPolicyActionResponse,
    PrivacyPolicyInfoResponse,
    ResetPasswordRequest,
    SendCodeRequest,
    TwoFactorProvidersResponse,
    UserCountryResponse,
    ValidateResetTokenRequest,
    ValidateResetTokenResponse,
    VerifyCodeRequest,
    VerifyCodeResponse,
    VerifyPhoneRequest,
} from '../types/login';

const TIMEOUT_MS = 100_000;

// ---------------------------------------------------------------------------
// URL helper — old code uses location.origin + '/' + Controller + '/' + Action
// ---------------------------------------------------------------------------

const makeUrl = (controller: string, action: string) => {
    return `${location.origin}/${controller}/${action}`;
};

// ---------------------------------------------------------------------------
// HTTP helpers — replicate LoginApiUtils.js exactly
// ---------------------------------------------------------------------------

const get = async <T>(url: string): Promise<T> => {
    const res = await send('GET', url, { noCache: true, timeout: TIMEOUT_MS });
    return res.body as T;
};

const post = async <T>(
    url: string,
    data: Record<string, unknown> = {},
): Promise<{ body: T; headers: Record<string, string> }> => {
    const res = await send('POST', url, { form: data });
    return { body: res.body as T, headers: res.headers };
};

const jsonPost = async <T>(url: string, data: Record<string, unknown> = {}): Promise<T> => {
    const res = await postJson(url, data);
    return res.body as T;
};

// ---------------------------------------------------------------------------
// API methods — match old LoginContext exactly
// ---------------------------------------------------------------------------

export const getLoginProviders = async (
    email: string,
    returnUrl: string,
): Promise<LoginProvidersResponse | null> => {
    const { body, headers } = await post<LoginProvidersResponse>(
        makeUrl('Login', 'GetLoginProviders'),
        { email, returnUrl },
    );
    // A single SSO provider comes back as a 200 with the provider's URL in Location.
    if (headers.location) {
        window.location.href = headers.location;
        return null;
    }
    return body;
};

export const postLogin = async (req: LoginRequest): Promise<LoginResponse> => {
    const { body } = await post<LoginResponse>(makeUrl('Login', 'Login'), {
        userName: req.email,
        password: req.password,
        turnstileToken: req.turnstileToken,
        returnUrl: req.returnUrl,
    });
    return body;
};

export const postForgotPassword = async (req: ForgotPasswordRequest): Promise<LoginResponse> => {
    const { body } = await post<LoginResponse>(makeUrl('Login', 'ForgotPassword'), {
        email: req.email,
        turnstileToken: req.turnstileToken,
    });
    return body;
};

export const postResetPassword = async (req: ResetPasswordRequest): Promise<LoginResponse> => {
    const { body } = await post<LoginResponse>(makeUrl('Login', 'ResetPassword'), {
        email: req.email,
        code: req.code,
        password: req.password,
        confirmPassword: req.confirmPassword,
        ignoreBreachWarning: req.ignoreBreachWarning,
    });
    return body;
};

export const validateResetToken = async (
    req: ValidateResetTokenRequest,
): Promise<ValidateResetTokenResponse> => {
    const { body } = await post<ValidateResetTokenResponse>(
        makeUrl('Login', 'ValidateResetPasswordToken'),
        {
            email: req.email,
            token: req.code,
        },
    );
    return body;
};

export const getTwoFactorProviders = async (): Promise<TwoFactorProvidersResponse> => {
    return get<TwoFactorProvidersResponse>(makeUrl('TwoFactor', 'GetTwoFactorProviders'));
};

export const sendTwoFactorCode = async (req: SendCodeRequest): Promise<LoginResponse> => {
    const { body } = await post<LoginResponse>(makeUrl('TwoFactor', 'SendCode'), {
        provider: req.provider,
    });
    return body;
};

export const verifyTwoFactorCode = async (req: VerifyCodeRequest): Promise<VerifyCodeResponse> => {
    const { body } = await post<VerifyCodeResponse>(makeUrl('TwoFactor', 'VerifyCode'), {
        code: req.code,
        provider: req.provider,
        rememberBrowser: req.rememberBrowser,
        returnUrl: req.returnUrl,
    });
    return body;
};

export const getUserCountry = async (): Promise<UserCountryResponse> => {
    return get<UserCountryResponse>(makeUrl('TwoFactor', 'GetUserCountry'));
};

export const registerPhone = async (phoneNumber: string): Promise<LoginResponse> => {
    return jsonPost<LoginResponse>(makeUrl('TwoFactor', 'AddPhoneNumber'), {
        number: phoneNumber,
    });
};

export const verifyPhone = async (req: VerifyPhoneRequest): Promise<LoginResponse> => {
    return jsonPost<LoginResponse>(makeUrl('TwoFactor', 'VerifyPhoneNumber'), {
        code: req.code,
        enableTwoFactor: true,
        phoneNumber: req.phoneNumber,
    });
};

// ---------------------------------------------------------------------------
// Privacy policy
// ---------------------------------------------------------------------------

export const getPrivacyPolicyInfo = async (): Promise<PrivacyPolicyInfoResponse> => {
    return get<PrivacyPolicyInfoResponse>(makeUrl('PrivacyPolicy', 'Info'));
};

export const acceptPrivacyPolicy = async (
    documents: PolicyDocumentAcceptance[],
): Promise<PrivacyPolicyActionResponse> => {
    return jsonPost<PrivacyPolicyActionResponse>(makeUrl('PrivacyPolicy', 'Accept'), {
        documents,
    });
};

export const declinePrivacyPolicy = async (): Promise<PrivacyPolicyActionResponse> => {
    return jsonPost<PrivacyPolicyActionResponse>(makeUrl('PrivacyPolicy', 'Decline'));
};

// ---------------------------------------------------------------------------
// CMS Content
// ---------------------------------------------------------------------------

export type CmsContentItem = {
    category: string;
    image: string;
    link: string;
    linkText: string;
    paragraph: string;
    title: string;
    videoUrl: string;
};

export type BeamerUpdate = {
    id: string;
    title: string;
    content: string;
    date: string;
    postUrl: string;
    category: string;
    image: string;
};

type CmsContentResponse = {
    content: CmsContentItem[];
    updates: BeamerUpdate[];
};

export const getLoginContent = async (): Promise<CmsContentResponse> => {
    const data = await get<CmsContentResponse>(makeUrl('CmsContent', 'GetLoginContent'));
    return {
        content: data.content ?? [],
        updates: data.updates ?? [],
    };
};
